import { useNavigate } from "react-router-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import React, {
    useState,
    useRef,
    useEffect,
    useCallback,
} from "react";
import {
    Box,
    Typography,
    TextField,
    alpha,
    Chip,
    IconButton,
    Tooltip,
} from "@mui/material";
import {
    Send,
    Mic,
    MicOff,
    CheckCircleOutlined,
    Description,
    PictureAsPdf,
    ContentCopy,
    RestartAlt,
    FiberManualRecord,
    Upload,
} from "@mui/icons-material";

import CustomHeader from "../../components/common/CustomHeader";
import CustomButton from "../../components/common/CustomButton";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import { getPlanExhaustMessage } from "../../components/common/StatusDictionary";

import CMNB from "../../assets/images/merubot_logo.png";
import CMSNB from "../../assets/images/merubot_icon.png";

import { jdGeneratorApi } from "../../api/JD_GeneratorApi";
import { jdApi } from "../../api/jobdescriptionApi";
import { useAuth } from "../../contexts/AuthContext";

// ─────────────────────────────────────────────────────────────────────────────
// PDF Export
// ─────────────────────────────────────────────────────────────────────────────

const company_logo = CMNB;

const toBase64 = (url) =>
    new Promise((resolve, reject) => {
        const img = new Image();

        img.crossOrigin = "Anonymous";

        img.onload = () => {
            try {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");

                canvas.width = img.naturalWidth || img.width;
                canvas.height = img.naturalHeight || img.height;

                ctx.drawImage(img, 0, 0);

                // JPEG works more reliably with jsPDF
                const dataURL = canvas.toDataURL("image/jpeg", 1.0);

                resolve(dataURL);
            } catch (err) {
                reject(err);
            }
        };

        img.onerror = (err) => {
            console.error("Image load failed:", err);
            reject(err);
        };

        // cache buster for S3/CDN
        img.src = `${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}`;
    });

async function exportJDtoPDF(jdRaw, logoUrl) {
    const { jsPDF } = await import("jspdf");

    const d = typeof jdRaw === "string" ? JSON.parse(jdRaw) : jdRaw;

    const doc = new jsPDF({
        unit: "mm",
        format: "a4",
    });

    const PAGE_W = 210;
    const PAGE_H = 297;
    const MARGIN_X = 18;
    const CONTENT_W = PAGE_W - MARGIN_X * 2;

    const PURPLE = [102, 126, 234];
    const NAVY = [26, 43, 74];
    const WHITE = [255, 255, 255];
    const BODY_BG = [248, 249, 251];
    const RULE = [208, 213, 221];
    const DARK = [45, 45, 45];

    let y = 0;

    const checkPage = (needed = 8) => {
        if (y + needed > PAGE_H - 15) {
            doc.addPage();
            y = 18;
        }
    };

    const rule = (color = RULE, thickness = 0.3) => {
        doc.setDrawColor(...color);
        doc.setLineWidth(thickness);
        doc.line(MARGIN_X, y, PAGE_W - MARGIN_X, y);
        y += 4;
    };

    const sectionHeader = (label) => {
        checkPage(12);

        y += 2;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(...PURPLE);

        doc.text(label.toUpperCase(), MARGIN_X, y);

        y += 2;

        rule(NAVY, 0.4);
    };

    const bulletLines = (items) => {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9.5);
        doc.setTextColor(...DARK);

        const BULLET_GAP = 4;

        items.forEach((item) => {
            checkPage(10);

            doc.text("•", MARGIN_X, y);

            doc.text(item, MARGIN_X + BULLET_GAP, y, {
                maxWidth: CONTENT_W - BULLET_GAP,
                align: "justify",
            });

            const textHeight = doc.getTextDimensions(item, {
                maxWidth: CONTENT_W - BULLET_GAP,
            }).h;

            y += textHeight + 1.5;
        });
    };

    const bodyText = (text) => {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9.5);
        doc.setTextColor(...DARK);

        checkPage(8);

        doc.text(text, MARGIN_X, y, {
            maxWidth: CONTENT_W,
            align: "justify",
        });

        y += doc.getTextDimensions(text, {
            maxWidth: CONTENT_W,
        }).h + 1;
    };

    // =========================
    // HEADER
    // =========================

    doc.setFillColor(...BODY_BG);
    doc.rect(0, -5, PAGE_W, 20, "F");

    try {
        const finalLogo = logoUrl || company_logo;

        if (finalLogo) {
            // console.log("Loading logo:", finalLogo);

            const logoBase64 = await toBase64(finalLogo);

            // console.log("Logo converted successfully");

            doc.addImage(
                logoBase64,
                "JPEG",
                10,
                2,
                30,
                12
            );
        }
    } catch (e) {
        console.warn("Logo not loaded", e);
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(...DARK);

    doc.text(
        d.title || "Job Description",
        PAGE_W / 2,
        10,
        { align: "center" }
    );

    y = 20;

    // =========================
    // ABOUT
    // =========================

    if (d.about_the_job) {
        sectionHeader("About the Job");

        bodyText(d.about_the_job);

        y += 2;
    }

    // =========================
    // JOB DETAILS
    // =========================

    const detailPairs = [
        d.experience && ["Experience", d.experience],
        d.location && ["Location", d.location],
        d.employment_type && ["Employment Type", d.employment_type],
        d.work_mode && ["Work Mode", d.work_mode],
    ].filter(Boolean);

    if (detailPairs.length) {
        sectionHeader("Job Details");

        detailPairs.forEach(([key, val]) => {
            checkPage(7);

            doc.setFont("helvetica", "bold");
            doc.setFontSize(9);
            doc.setTextColor(...DARK);

            doc.text(`${key}: ${val}`, MARGIN_X, y, {
                maxWidth: CONTENT_W,
                align: "left",
            });

            y += 6;
        });

        y += 2;
    }

    // =========================
    // SKILLS
    // =========================

    if (d.skills?.length) {
        sectionHeader("Skills");

        const pillH = 6.5;
        const pillPad = 3;
        const pillGap = 2.5;

        const pillBg = PURPLE.map((c) =>
            Math.min(255, c + 130)
        );

        let px = MARGIN_X;

        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");

        d.skills.forEach((skill) => {
            const tw = doc.getTextWidth(skill);
            const pillW = tw + pillPad * 2;

            if (px + pillW > PAGE_W - MARGIN_X) {
                px = MARGIN_X;
                y += pillH + pillGap;

                checkPage(pillH + 4);
            }

            doc.setFillColor(...pillBg);

            doc.roundedRect(
                px,
                y,
                pillW,
                pillH,
                1.5,
                1.5,
                "F"
            );

            doc.setTextColor(...PURPLE);

            doc.text(
                skill,
                px + pillPad,
                y + pillH - 1.8
            );

            px += pillW + pillGap;
        });

        y += pillH + 8;
    }

    // =========================
    // RESPONSIBILITIES
    // =========================

    if (d.responsibilities?.length) {
        sectionHeader("Responsibilities");

        bulletLines(d.responsibilities);

        y += 2;
    }

    // =========================
    // QUALIFICATIONS
    // =========================

    if (d.qualifications?.length) {
        sectionHeader("Qualifications");

        bulletLines(d.qualifications);

        y += 2;
    }

    // =========================
    // FOOTER
    // =========================

    const totalPages = doc.getNumberOfPages();

    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);

        doc.setFillColor(...PURPLE);

        doc.rect(0, PAGE_H - 10, PAGE_W, 10, "F");

        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(...WHITE);

        doc.text(
            `${d.title || "Job Description"}`,
            MARGIN_X,
            PAGE_H - 3.5
        );

        doc.text(
            "Powered by MeruBot",
            PAGE_W / 2,
            PAGE_H - 3.5,
            { align: "center" }
        );

        doc.text(
            `·  Page ${i} of ${totalPages}`,
            PAGE_W - MARGIN_X,
            PAGE_H - 3.5,
            { align: "right" }
        );
    }

    // =========================
    // SAVE
    // =========================

    doc.save(
        `${(d.title || "job-description")
            .replace(/\s+/g, "-")}.pdf`
    );
}
// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function getTitle(raw) {
    try {
        const d = typeof raw === "string" ? JSON.parse(raw) : raw;
        return d?.title || "Job Description";
    } catch { return "Job Description"; }
}

function jdToHtml(raw) {
    try {
        const d = typeof raw === "string" ? JSON.parse(raw) : raw;
        const lines = [];
        if (d.title) lines.push(`<p><strong>${d.title}</strong></p><p></p>`);
        if (d.about_the_job) {
            lines.push(`<p><strong>About the Job</strong></p>`);
            lines.push(`<p>${d.about_the_job}</p><p></p>`);
        }
        const details = [
            d.experience && `Experience: ${d.experience}`,
            d.location && `Location: ${d.location}`,
            d.employment_type && `Employment Type: ${d.employment_type}`,
            d.work_mode && `Work Mode: ${d.work_mode}`,
        ].filter(Boolean);
        if (details.length) {
            lines.push(`<p><strong>Job Details</strong></p>`);
            lines.push(`<ul>${details.map((i) => `<li>${i}</li>`).join("")}</ul><p></p>`);
        }
        if (d.skills?.length) {
            lines.push(`<p><strong>Skills</strong></p>`);
            lines.push(`<ul>${d.skills.map((s) => `<li>${s}</li>`).join("")}</ul><p></p>`);
        }
        if (d.responsibilities?.length) {
            lines.push(`<p><strong>Responsibilities</strong></p>`);
            lines.push(`<ul>${d.responsibilities.map((r) => `<li>${r}</li>`).join("")}</ul><p></p>`);
        }
        if (d.qualifications?.length) {
            lines.push(`<p><strong>Qualifications</strong></p>`);
            lines.push(`<ul>${d.qualifications.map((q) => `<li>${q}</li>`).join("")}</ul><p></p>`);
        }
        return lines.join("");
    } catch {
        return `<p>${typeof raw === "string" ? raw : JSON.stringify(raw)}</p>`;
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// BotAvatar — shared icon with transparent background
// ─────────────────────────────────────────────────────────────────────────────

function BotAvatar({ size = 28, imgSize = 24, mr = 0 }) {
    return (
        <Box
            sx={{
                width: size, height: size,
                borderRadius: "8px",
                background: "transparent",   // ← no camouflage
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
                ...(mr ? { mr } : {}),
            }}
        >
            <img src={CMSNB} alt="bot" style={{ width: imgSize, height: imgSize, objectFit: "contain" }} />
        </Box>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Bubble & ThinkingBubble
// ─────────────────────────────────────────────────────────────────────────────

function Bubble({ role, content }) {
    const isUser = role === "user";
    return (
        <Box sx={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", mb: 1.5 }}>
            {!isUser && <BotAvatar mr={1} />}
            <Box
                sx={{
                    maxWidth: "78%", px: 2, py: 1.25,
                    borderRadius: isUser ? "16px 16px 4px 16px" : "4px 16px 16px 16px",
                    background: isUser
                        ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                        : alpha("#667eea", 0.07),
                    border: isUser ? "none" : `1px solid ${alpha("#667eea", 0.15)}`,
                    color: isUser ? "#fff" : "text.primary",
                    fontSize: "0.875rem", lineHeight: 1.6,
                    whiteSpace: "pre-wrap", wordBreak: "break-word",
                }}
            >
                {content}
            </Box>
        </Box>
    );
}

function ThinkingBubble() {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <BotAvatar />
            <Box
                sx={{
                    px: 2, py: 1.25, borderRadius: "4px 16px 16px 16px",
                    background: alpha("#667eea", 0.07),
                    border: `1px solid ${alpha("#667eea", 0.15)}`,
                    display: "flex", gap: 0.5, alignItems: "center",
                }}
            >
                {[0, 0.2, 0.4].map((delay) => (
                    <Box
                        key={delay}
                        sx={{
                            width: 7, height: 7, borderRadius: "50%", background: "#667eea",
                            animation: "bounce 1s infinite", animationDelay: `${delay}s`,
                            "@keyframes bounce": {
                                "0%, 80%, 100%": { transform: "scale(0.6)", opacity: 0.4 },
                                "40%": { transform: "scale(1)", opacity: 1 },
                            },
                        }}
                    />
                ))}
            </Box>
        </Box>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// JDPanel
// ─────────────────────────────────────────────────────────────────────────────

function JDPanel({ jdRaw, isFinalized, onFinalize, isFinalizing, savedJDId, onUpload, isUploadingJD }) {
    const { companyLogo } = useAuth();
    const title = jdRaw ? getTitle(jdRaw) : "";

    const editor = useEditor({
        extensions: [StarterKit],
        content: jdRaw ? jdToHtml(jdRaw) : "",
        editable: !isFinalized,
        editorProps: {
            attributes: {
                style: [
                    "outline: none",
                    "font-family: inherit",
                    "font-size: 0.875rem",
                    "line-height: 1.6",
                    "color: inherit",
                    "min-height: 100%",
                    "padding: 16px 20px",
                ].join(";"),
            },
        },
    });

    const prevRaw = useRef(null);
    useEffect(() => {
        if (editor && jdRaw && jdRaw !== prevRaw.current) {
            prevRaw.current = jdRaw;
            editor.commands.setContent(jdToHtml(jdRaw));
        }
    }, [editor, jdRaw]);

    useEffect(() => {
        if (editor) editor.setEditable(!isFinalized);
    }, [editor, isFinalized]);

    const handleCopy = () => {
        const text = editor ? editor.getText() : "";
        navigator.clipboard.writeText(text);
    };

    const handleExportPDF = async () => {
        try {
            if (!editor) return;
            const json = editor.getJSON();
            const nodes = json.content || [];

            const result = {
                title: "", about_the_job: "", skills: [],
                responsibilities: [], qualifications: [],
                experience: "", location: "", employment_type: "", work_mode: "",
            };

            const nodeText = (node) =>
                (node.content || []).map((n) => n.text || "").join("").trim();

            const isBold = (node) =>
                node.type === "paragraph" &&
                (node.content || []).some((n) =>
                    (n.marks || []).some((m) => m.type === "bold")
                );

            let currentSection = null;

            nodes.forEach((node) => {
                if (node.type === "paragraph") {
                    const text = nodeText(node);
                    if (!text) return;
                    if (isBold(node)) {
                        const label = text.toLowerCase();
                        if (label === "about the job") currentSection = "about";
                        else if (label === "job details") currentSection = "details";
                        else if (label === "skills") currentSection = "skills";
                        else if (label === "responsibilities") currentSection = "responsibilities";
                        else if (label === "qualifications") currentSection = "qualifications";
                        else { currentSection = null; result.title = text; }
                    } else {
                        if (currentSection === "about")
                            result.about_the_job += (result.about_the_job ? " " : "") + text;
                    }
                } else if (node.type === "bulletList") {
                    const items = (node.content || []).map((li) =>
                        (li.content || []).map((p) => nodeText(p)).join(" ").trim()
                    ).filter(Boolean);

                    if (currentSection === "details") {
                        items.forEach((item) => {
                            if (item.startsWith("Experience:"))
                                result.experience = item.replace("Experience:", "").trim();
                            else if (item.startsWith("Location:"))
                                result.location = item.replace("Location:", "").trim();
                            else if (item.startsWith("Employment Type:"))
                                result.employment_type = item.replace("Employment Type:", "").trim();
                            else if (item.startsWith("Work Mode:"))
                                result.work_mode = item.replace("Work Mode:", "").trim();
                        });
                    } else if (currentSection === "skills") result.skills = items;
                    else if (currentSection === "responsibilities") result.responsibilities = items;
                    else if (currentSection === "qualifications") result.qualifications = items;
                }
            });

            await exportJDtoPDF(result, companyLogo || company_logo);
        } catch (e) {
            console.error("PDF export error:", e);
        }
    };

    if (!jdRaw) {
        return (
            <Box
                sx={{
                    height: "100%", display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center", gap: 2,
                    color: "text.disabled", borderRadius: "16px",
                    border: `2px dashed ${alpha("#667eea", 0.2)}`,
                    background: alpha("#667eea", 0.02),
                }}
            >
                <Description sx={{ fontSize: 48, opacity: 0.3 }} />
                <Typography variant="body2" sx={{ opacity: 0.6, textAlign: "center" }}>
                    Your generated JD will appear here
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: "100%", display: "flex", flexDirection: "column",
                borderRadius: "16px", border: `1px solid ${alpha("#667eea", 0.15)}`,
                overflow: "hidden", background: "#fff",
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    px: 2.5, py: 1.75, display: "flex", alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: `1px solid ${alpha("#667eea", 0.1)}`,
                    background: `linear-gradient(135deg, ${alpha("#667eea", 0.04)} 0%, ${alpha("#764ba2", 0.04)} 100%)`,
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Description sx={{ fontSize: 18, color: "#667eea" }} />
                    <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ maxWidth: 180 }}>
                        {title}
                    </Typography>
                    {isFinalized ? (
                        <Chip
                            icon={<CheckCircleOutlined sx={{ fontSize: "13px !important" }} />}
                            label="Saved" size="small"
                            sx={{
                                height: 20, fontSize: "0.68rem", fontWeight: 700,
                                background: alpha("#10b981", 0.12), color: "#10b981",
                                border: `1px solid ${alpha("#10b981", 0.3)}`,
                                "& .MuiChip-label": { px: 0.75 },
                            }}
                        />
                    ) : (
                        <Chip
                            label="Editable" size="small"
                            sx={{
                                height: 20, fontSize: "0.68rem", fontWeight: 700,
                                background: alpha("#667eea", 0.1), color: "#667eea",
                                border: `1px solid ${alpha("#667eea", 0.25)}`,
                                "& .MuiChip-label": { px: 0.75 },
                            }}
                        />
                    )}
                </Box>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Tooltip title="Copy text">
                        <IconButton size="small" onClick={handleCopy} sx={{ color: "text.secondary" }}>
                            <ContentCopy sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export PDF">
                        <IconButton size="small" onClick={handleExportPDF} sx={{ color: "text.secondary" }}>
                            <PictureAsPdf sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Tiptap Editor */}
            <Box
                sx={{
                    flex: 1, overflowY: "auto",
                    scrollbarWidth: "thin",
                    scrollbarColor: `${alpha("#667eea", 0.3)} transparent`,
                    "&::-webkit-scrollbar": { width: 5 },
                    "&::-webkit-scrollbar-thumb": { background: alpha("#667eea", 0.3), borderRadius: 3 },
                    "& .tiptap": { outline: "none", minHeight: "100%" },
                    "& .tiptap p": { margin: "0 0 4px 0", fontSize: "0.875rem", lineHeight: 1.6, fontFamily: "inherit", textAlign: "justify" },
                    "& .tiptap strong": { fontWeight: 700, color: "#667eea", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginTop: "14px", marginBottom: "4px" },
                    "& .tiptap ul": { margin: "0 0 4px 0", paddingLeft: "18px" },
                    "& .tiptap li": { fontSize: "0.875rem", lineHeight: 1.6, fontFamily: "inherit", marginBottom: "2px" },
                    "& .tiptap:focus": { caretColor: "#667eea" },
                    ...(!isFinalized && { "&:hover": { background: alpha("#667eea", 0.01) } }),
                }}
            >
                <EditorContent editor={editor} />
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    px: 2.5, py: 1.75,
                    borderTop: `1px solid ${alpha("#667eea", 0.1)}`,
                    background: alpha("#fff", 0.95), flexShrink: 0,
                }}
            >
                {isFinalized ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
                            <CheckCircleOutlined sx={{ fontSize: 18, color: "#10b981" }} />
                            <Typography variant="body2" fontWeight={600} sx={{ color: "#10b981" }}>
                                JD finalized and saved to database
                            </Typography>
                        </Box>
                        {/* Upload JD button */}
                        <CustomButton
                            onClick={onUpload}
                            disabled={isUploadingJD}
                            fullWidth
                            sx={{
                                display: "flex", alignItems: "center", gap: 0.75,
                                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                "&:hover": { opacity: 0.9 },
                            }}
                        >
                            <Upload sx={{ fontSize: 16 }} />
                            {isUploadingJD ? "Uploading..." : savedJDId ? "Open in Analysis" : "Upload JD"}
                        </CustomButton>
                    </Box>
                ) : (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="caption" sx={{ color: "text.disabled" }}>
                            Click anywhere in the JD to edit · Use chat to refine
                        </Typography>
                        <CustomButton onClick={onFinalize} disabled={isFinalizing} size="small" sx={{ minWidth: 130 }}>
                            {isFinalizing ? "Saving..." : "✓ Finalize & Save"}
                        </CustomButton>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

export default function JDGeneratorPage() {
    const navigate = useNavigate();

    const [messages, setMessages] = useState([{
        role: "agent",
        content: "Hi! Describe the role you want a JD for.\n\nExample: \"Senior React Developer at a fintech startup, 4+ years experience, hybrid, Pune\"",
    }]);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [phase, setPhase] = useState("idle");
    const [clarifyingQuestions, setClarifyingQuestions] = useState([]);
    const [clarificationAnswers, setClarificationAnswers] = useState({});
    const [currentQIdx, setCurrentQIdx] = useState(0);
    const [generatedJD, setGeneratedJD] = useState(null);
    const [savedJDId, setSavedJDId] = useState(null);
    const [isFinalized, setIsFinalized] = useState(false);
    const [isFinalizing, setIsFinalizing] = useState(false);
    const [isUploadingJD, setIsUploadingJD] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef(null);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "success" });

    const speechSupported =
        typeof window !== "undefined" &&
        ("SpeechRecognition" in window || "webkitSpeechRecognition" in window);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isLoading]);

    const notify = useCallback((message, type = "success") => {
        setSnackbar({ open: true, message, type });
    }, []);

    const addMessage = useCallback((role, content) => {
        setMessages((prev) => [...prev, { role, content }]);
    }, []);

    const toggleListening = useCallback(() => {
        if (!speechSupported) return;

        if (isListening) {
            recognitionRef.current?.stop();
            recognitionRef.current = null;
            setIsListening(false);
            return;
        }

        let finalTranscript = "";

        const startRecognition = () => {
            const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
            const rec = new SR();
            rec.lang = "en-IN";
            rec.interimResults = true;
            rec.continuous = true;

            rec.onresult = (e) => {
                let interim = "";
                for (let i = e.resultIndex; i < e.results.length; i++) {
                    const transcript = e.results[i][0].transcript;
                    if (e.results[i].isFinal) {
                        finalTranscript += transcript + " ";
                    } else {
                        interim += transcript;
                    }
                }
                setInput(finalTranscript + interim);
            };

            rec.onend = () => {
                // Only restart if user hasn't manually stopped (ref is still set)
                if (recognitionRef.current) {
                    setTimeout(startRecognition, 100);
                }
            };

            rec.onerror = (e) => {
                if (e.error === "no-speech") return; // ignore, onend will restart
                if (e.error === "aborted") return; // user stopped manually
                console.warn("Speech error:", e.error);
            };

            recognitionRef.current = rec;
            rec.start();
        };

        setIsListening(true);
        startRecognition();

    }, [isListening, speechSupported]);

    const handleReset = useCallback(() => {
        setMessages([{
            role: "agent",
            content: "Hi! Describe the role you want a JD for.\n\nExample: \"Senior React Developer at a fintech startup, 4+ years experience, hybrid, Pune\"",
        }]);
        setInput(""); setSessionId(null); setPhase("idle");
        setClarifyingQuestions([]); setClarificationAnswers({}); setCurrentQIdx(0);
        setGeneratedJD(null); setIsFinalized(false); setIsFinalizing(false); setSavedJDId(null);
    }, []);

    const handleFinalize = useCallback(async () => {
        if (!sessionId || isFinalizing || isFinalized) return;
        setIsFinalizing(true);
        try {
            const response = await jdGeneratorApi.finalizeJD(sessionId);
            const data = response?.data ?? response;
            if (data?.jd_id) setSavedJDId(data.jd_id);   // ← consistent casing
            setIsFinalized(true);
            setPhase("finalized");
            addMessage("agent", "✅ JD finalized and saved to database!");
        } catch (err) {
            console.error("Finalize error:", err);
            notify("Failed to finalize JD. Please try again.", "error");
        } finally {
            setIsFinalizing(false);
        }
    }, [sessionId, isFinalizing, isFinalized, addMessage, notify]);

    const parseGeneratedJD = useCallback((raw) => {
        if (!raw) return null;
        if (typeof raw === "string") {
            try { return JSON.parse(raw); } catch { return { summary: raw }; }
        }
        return raw;
    }, []);

    const buildRecruiterJDPayload = useCallback((rawJD) => {
        const jd = parseGeneratedJD(rawJD) || {};
        const skills = Array.isArray(jd.skills) ? jd.skills.join(", ") : jd.skills || "";
        const responsibilities = Array.isArray(jd.responsibilities) ? jd.responsibilities.join("\n") : jd.responsibilities || "";
        const qualifications = Array.isArray(jd.qualifications) ? jd.qualifications.join("\n") : jd.qualifications || "";
        return {
            job_title: jd.title || jd.job_title || jd.role || "",
            company: jd.company || jd.employer || "",
            job_type: jd.experience || jd.job_type || "",
            summary: jd.about_the_job || jd.summary || jd.description || "",
            domain: jd.domain || "",
            status: "active",
            mode: "dynamic",
            jd_url: jd.jd_url || "",
            skill: { id: jd.skill?.id || "", keySkills: skills, additionalSkills: jd.additionalSkills || jd.additional_skills || "" },
            experience: { id: jd.experience?.id || "", responsibilities },
            education: { id: jd.education?.id || "", education: qualifications },
        };
    }, [parseGeneratedJD]);

    const handleSubmit = useCallback(async () => {
        const text = input.trim();                                     // ← single declaration
        if (!text || isLoading || phase === "finalized") return;

        setInput(""); addMessage("user", text); setIsLoading(true);   // ← single call

        try {
            if (phase === "idle") {
                addMessage("agent", "🔍 Analysing your prompt...");
                const response = await jdGeneratorApi.startSession("recruiter", text);
                // console.log(response)

                const data = response?.data ?? response;
                if (!data?.session_id) throw new Error(response?.message || "Failed to start session.");
                setSessionId(data.session_id);

                if (data.is_vague && data.clarifying_questions?.length > 0) {
                    setClarifyingQuestions(data.clarifying_questions);
                    setCurrentQIdx(0); setPhase("clarifying");
                    setMessages((prev) => [
                        ...prev.slice(0, -1),
                        { role: "agent", content: "I need a few more details to create a great JD:\n\n" + data.clarifying_questions[0] },
                    ]);
                } else {
                    setMessages((prev) => [...prev.slice(0, -1), { role: "agent", content: "✍️ Generating your JD..." }]);
                    const genResponse = await jdGeneratorApi.generateDirect(data.session_id);
                    const genData = genResponse?.data ?? genResponse;
                    if (!genData?.generated_jd) throw new Error(genResponse?.message || "Failed to generate JD.");
                    const jd = genData.generated_jd;
                    setGeneratedJD(jd); setPhase("refining");
                    setMessages((prev) => [
                        ...prev.slice(0, -1),
                        { role: "agent", content: `✅ JD generated for **${getTitle(jd)}**!\n\nReview it on the right. You can:\n• Ask me to refine it (e.g. "add remote work policy")\n• Click **Finalize & Save** when you're happy` },
                    ]);
                }

            } else if (phase === "clarifying") {
                const currentQ = clarifyingQuestions[currentQIdx];
                const updatedAnswers = { ...clarificationAnswers, [currentQ]: text };
                setClarificationAnswers(updatedAnswers);

                if (currentQIdx < clarifyingQuestions.length - 1) {
                    const next = currentQIdx + 1;
                    setCurrentQIdx(next);
                    addMessage("agent", `Got it! Next question:\n\n${clarifyingQuestions[next]}`);
                } else {
                    addMessage("agent", "✍️ Generating your JD with all the details...");
                    const clarifyResponse = await jdGeneratorApi.submitClarification(sessionId, updatedAnswers);
                    const clarifyData = clarifyResponse?.data ?? clarifyResponse;
                    if (!clarifyData?.generated_jd) throw new Error(clarifyResponse?.message || "Failed to submit clarifications.");
                    const jd = clarifyData.generated_jd;
                    setGeneratedJD(jd); setPhase("refining");
                    setMessages((prev) => [
                        ...prev.slice(0, -1),
                        { role: "agent", content: `✅ JD generated for **${getTitle(jd)}**!\n\nReview it on the right. You can:\n• Ask me to refine it (e.g. "add remote work policy")\n• Click **Finalize & Save** when you're happy` },
                    ]);
                }

            } else if (phase === "refining") {
                addMessage("agent", "🔄 Applying your refinement...");
                const refineResponse = await jdGeneratorApi.refineJD(sessionId, text);
                const refineData = refineResponse?.data ?? refineResponse;
                if (!refineData?.generated_jd) throw new Error(refineResponse?.message || "Failed to refine JD.");
                setGeneratedJD(refineData.generated_jd);
                setMessages((prev) => [
                    ...prev.slice(0, -1),
                    { role: "agent", content: `✅ Refinement #${refineData.refinement_count} applied! Check the updated JD on the right.` },
                ]);
            }
        } catch (err) {
            console.error("Submit error:", err);
            setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (last?.role === "agent" && ["🔍", "✍️", "🔄"].some((e) => last.content.startsWith(e)))
                    return prev.slice(0, -1);
                return prev;
            });

            // Check for insufficient credit error
            const errorMessage = err.message || "Please try again.";
            // console.log(err)
            setSnackbar({
                open: true,
                message: errorMessage,
                type: "error",
            });
        } finally {
            setIsLoading(false);
            inputRef.current?.focus();
        }
    }, [input, isLoading, phase, sessionId, clarifyingQuestions, clarificationAnswers, currentQIdx, addMessage, setSnackbar]);

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
    };

    // Upload JD to analysis page
    const uploadAndNavigateToAnalysis = useCallback(async () => {
        if (!generatedJD || isUploadingJD) return;
        if (savedJDId) {
            navigate("/recruiter/analysis", { state: { id: savedJDId } });
            return;
        }
        const payload = buildRecruiterJDPayload(generatedJD);
        setIsUploadingJD(true);
        try {
            const response = await jdApi.createRecruiterJD(payload);
            if (!response || response.status === 0 || response.status >= 400)
                throw new Error(response?.message || "Failed to upload JD.");
            const createdId = response?.data?.id || response?.id || response?.job_id || response?.data?.job_id;
            if (createdId) {
                setSavedJDId(createdId);
                notify("JD uploaded successfully. Redirecting to analysis...");
                navigate("/recruiter/analysis", { state: { id: createdId } });
                return;
            }
            throw new Error("JD uploaded but no id returned.");
        } catch (error) {
            console.error("Upload JD error:", error);
            notify(error.message || "Could not upload JD.", "error");
        } finally {
            setIsUploadingJD(false);
        }
    }, [generatedJD, isUploadingJD, savedJDId, buildRecruiterJDPayload, navigate, notify]);

    const getPlaceholder = () => {
        if (phase === "finalized") return "JD is finalized.";
        if (phase === "clarifying") return "Answer the question above...";
        if (phase === "refining") return 'Request a change... (e.g. "add a benefits section")';
        return 'Describe the role... (e.g. "Senior Django developer, Pune, 5+ yrs")';
    };

    const chipMap = {
        clarifying: { label: "Gathering details", color: "#f59e0b" },
        refining: { label: "Refinement mode", color: "#667eea" },
        finalized: { label: "Finalized ✓", color: "#10b981" },
    };
    const chip = chipMap[phase] || null;

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CustomHeader title="JD Generator" />

            <Box
                sx={{
                    flex: 1, display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 2.5, p: { xs: 2, md: 3 }, overflow: "hidden", minHeight: 0,
                }}
            >
                {/* ════ LEFT: Chat ════ */}
                <Box sx={{ display: "flex", flexDirection: "column", borderRadius: "16px", border: `1px solid ${alpha("#667eea", 0.15)}`, overflow: "hidden", background: "#fff", minHeight: 0 }}>

                    {/* Chat header */}
                    <Box sx={{ px: 2.5, py: 1.75, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${alpha("#667eea", 0.1)}`, background: `linear-gradient(135deg, ${alpha("#667eea", 0.04)} 0%, ${alpha("#764ba2", 0.04)} 100%)`, flexShrink: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <BotAvatar />
                            <Typography variant="subtitle2" fontWeight={700}>JD Agent</Typography>
                            {chip && (
                                <Chip label={chip.label} size="small" sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700, background: alpha(chip.color, 0.12), color: chip.color, border: `1px solid ${alpha(chip.color, 0.3)}`, "& .MuiChip-label": { px: 0.75 } }} />
                            )}
                        </Box>
                        <Tooltip title="Start new JD">
                            <IconButton size="small" onClick={handleReset} sx={{ color: "text.secondary" }}>
                                <RestartAlt sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* Messages */}
                    <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2, scrollbarWidth: "thin", scrollbarColor: `${alpha("#667eea", 0.3)} transparent`, "&::-webkit-scrollbar": { width: 5 }, "&::-webkit-scrollbar-thumb": { background: alpha("#667eea", 0.3), borderRadius: 3 } }}>
                        {messages.map((msg, idx) => (
                            <Bubble key={idx} role={msg.role} content={msg.content} />
                        ))}
                        {isLoading && <ThinkingBubble />}
                        <div ref={messagesEndRef} />
                    </Box>

                    {/* Input area */}
                    <Box sx={{ px: 2, py: 1.75, borderTop: `1px solid ${alpha("#667eea", 0.1)}`, background: alpha("#fff", 0.95), flexShrink: 0 }}>
                        {isListening && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                                <FiberManualRecord sx={{ fontSize: 10, color: "#e53935", animation: "pulse 1s infinite", "@keyframes pulse": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.3 } } }} />
                                <Typography variant="caption" sx={{ color: "#e53935", fontWeight: 600 }}>Listening... speak now</Typography>
                            </Box>
                        )}
                        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                            <TextField
                                inputRef={inputRef} fullWidth multiline maxRows={4} size="small"
                                placeholder={getPlaceholder()} value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                disabled={phase === "finalized" || isLoading}
                                variant="outlined"
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", background: alpha("#667eea", 0.03), fontSize: "0.875rem", "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#667eea", borderWidth: "1.5px" } }, "& .MuiOutlinedInput-notchedOutline": { borderColor: alpha("#667eea", 0.2) } }}
                            />
                            {speechSupported && (
                                <Tooltip title={isListening ? "Stop listening" : "Voice input"}>
                                    <IconButton onClick={toggleListening} disabled={phase === "finalized"} sx={{ borderRadius: "10px", border: `1px solid ${alpha(isListening ? "#e53935" : "#667eea", 0.3)}`, background: isListening ? alpha("#e53935", 0.08) : alpha("#667eea", 0.06), color: isListening ? "#e53935" : "#667eea", "&:hover": { background: isListening ? alpha("#e53935", 0.15) : alpha("#667eea", 0.12) } }}>
                                        {isListening ? <MicOff sx={{ fontSize: 18 }} /> : <Mic sx={{ fontSize: 18 }} />}
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title="Send (Enter)">
                                <span>
                                    <IconButton onClick={handleSubmit} disabled={!input.trim() || isLoading || phase === "finalized"} sx={{ borderRadius: "10px", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "#fff", "&:hover": { opacity: 0.9 }, "&.Mui-disabled": { background: alpha("#667eea", 0.15), color: alpha("#667eea", 0.4) } }}>
                                        <Send sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>

                {/* ════ RIGHT: JD Preview ════ */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minHeight: 0 }}>
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                        <JDPanel
                            jdRaw={generatedJD}
                            isFinalized={isFinalized}
                            onFinalize={handleFinalize}
                            isFinalizing={isFinalizing}
                            savedJDId={savedJDId}
                            onUpload={uploadAndNavigateToAnalysis}
                            isUploadingJD={isUploadingJD}
                        />
                    </Box>
                </Box>

            </Box>

            <CustomSnackbar
                open={snackbar.open} message={snackbar.message} severity={snackbar.type}
                onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
            />
        </Box>
    );
}